import React, { useEffect, useState } from "react";
import {
  Input,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { resetError, resetState } from "../controller/author-slice";
import {
  addAuthor,
  editAuthor,
  getAuthor,
  getAuthorCount,
} from "../services/author-services";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import ViewModal from "@components/custom-table/ViewModel";
import { Client } from "@helpers/helpers";
import { API_METHOD } from "@constants/constants";
import { url } from "@constants/url-constants";
import {
  authorSchema,
  authorUpdateSchema,
} from "../../../helpers/validation-schema";
import ImageFileUpload from "../../../components/custom-image/ImageFileUpload";
import { NAME_REGEX } from "../../../constants/constants";
import { handleInput } from "../../../helpers/helpers";
import CustomLabel from "../../../components/CustomLabel";

export const AddAuthor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const authorId = pathname.split("/")[3];
  const success = useSelector((state) => state.author.success);
  const author = useSelector((state) => state.author.authors);
  const loading = useSelector((state) => state.author.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.author.successMessage);
  const errorMessage = useSelector((state) => state.author.errorMessage);
  const [isLoading, setIsLoading] = useState(false);
  const [viewModel, setViewModel] = useState(false);
  const [fileInfo, extractFileMetadata] = ImageFileUpload();
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(route === "edit" ? authorUpdateSchema : authorSchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getAuthorCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, []);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getAuthor(authorId));
    }
  }, [authorId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/author?current_page=${pageQuery}`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (author) {
      setValue("sorting", author?.sorting);
      setValue("name", author?.name);
    }
  }, [author, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    let tempImageUrl = "";
    if (data.image) {
      const generatePresignedUrl = await handleImageUpload(data.image);
      if (generatePresignedUrl.imageTemp) {
        tempImageUrl = generatePresignedUrl.imageTemp;
      } else {
        //show error message
        setIsLoading(false);
        // hide loader or enable button
      }
    }

    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        thumbnail: { image_url: tempImageUrl, ...fileInfo },
      };
      saveAuthorForm(formattedData);
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
        thumbnail: tempImageUrl
          ? { image_url: tempImageUrl, ...fileInfo }
          : author?.thumbnail,
      };
      saveAuthorForm(formattedData, authorId);
    }
  };

  const saveAuthorForm = (formattedData, authorId = "") => {
    if (authorId) {
      dispatch(editAuthor({ id: authorId, data: formattedData }));
    } else {
      dispatch(addAuthor(formattedData));
    }
  };

  const handleImageUpload = async (file) => {
    const fileExtension = {
      extensions: [file?.name.split(".").pop()?.toLowerCase()],
    };

    try {
      const response = await Client(
        API_METHOD.POST,
        url.presigned_url,
        fileExtension
      );
      const presignedUrl = response.data.data.items[0].preSignedUrl;

      const imageTemp = response.data.data.items[0].fileName;

      const saveImage = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        redirect: "follow",
      });
      if (saveImage.status === 200) {
        return { imageTemp };
      } else {
        return { type: "error", message: "Failed to Upload Image" };
      }
    } catch (error) {
      return { type: "error", message: "Failed to Upload Image" };
    }
  };

  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };
  const handleViewImage = () => {
    setViewModel(true);
  };
  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          {viewModel && (
            <ViewModal
              show={viewModel}
              onViewClick={handleViewImage}
              onCloseClick={() => setViewModel(false)}
              data={author?.thumbnail?.image_url}
            />
          )}
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit Author"
                  : route === "view"
                  ? "View Author"
                  : "Create Author"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Name" isRequired>
                        Author Name
                      </CustomLabel>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Author name"
                              invalid={!!errors.name}
                              onInput={(e) => handleInput(e, NAME_REGEX)}
                              maxLength={NAME_MAXLENGTH}
                            />
                            {errors.name && (
                              <FormFeedback>{errors.name.message}</FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                    <CustomLabel htmlFor="Image" isRequired>
                        Image
                      </CustomLabel>

                      {route !== "add" ? (
                          <button
                            type="button"
                            className="view-button"
                            onClick={() => {
                              handleViewImage();
                            }}
                          >
                            View
                          </button>
                        ) : null}
                      {/* <Label for="image">
                        Image
                        {route !== "add" ? (
                          <Link
                            className="ms-2"
                            onClick={() => {
                              handleViewImage();
                            }}
                          >
                            View
                          </Link>
                        ) : null}
                      </Label> */}
                      <Controller
                        name="image"
                        control={control}
                        defaultValue={null}
                        render={({ field }) => (
                          <div>
                            <Input
                              type="file"
                              accept="image/jpeg,image/png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                field.onChange(file);
                                extractFileMetadata(file);
                              }}
                              invalid={!!errors.image}
                            />
                            {errors.image && (
                              <FormFeedback>
                                {errors.image.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/author?current_page=${pageQuery}`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button
                          type="submit"
                          color={isLoading ? "success" : "primary"}
                        >
                          {isLoading ? (
                            <>Loading...</>
                          ) : (
                            <>
                              {route === "edit"
                                ? "Edit Author"
                                : "Create Author"}
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddAuthor;
